"use client";

import {toolRequestBody} from "./toolRequestBody";

import {useTranslation} from "react-i18next";
import {humanizeToolField, isPresentationRecord, presentedFields, type PresentedField} from "@repo/ui/gailgpt/tool-presentation";
import {useTextScriptNames} from "@/hooks/useTextScriptNames";
import {formatOutcomeKeyLabel} from "@/utils/orgOutcomes/formatOutcomeKeyLabel";
import FrozenValue from "./FrozenValue";
import ToolPresentationDetails, {ReviewFields, type ToolDetailsProps} from "./ToolPresentationDetails";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

type RecordValue = Record<string, unknown>;
const configured = (value: unknown) => value != null && value !== "";

function RuleActions({actions}: {actions: RecordValue[]}) {
   const {t} = useTranslation("ask");
   const names = useTextScriptNames(actions.some(action => configured(action.textScriptId)));
   return <ol className="space-y-2">
      {occurrenceKeys(actions, valueKey).map(({item: action, key}, index) => {
         const extras = Object.fromEntries(Object.entries(action).filter(([key, value]) =>
            !["action", "delay", "maxAttempts", "textScriptId"].includes(key) || (key === "textScriptId" && configured(value))));
         return <li key={key} className="rounded-md bg-100 px-3 py-2.5 text-xs">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
               <span className="font-medium text-800">
                  {actions.length > 1 && <span className="mr-2 text-500">{index + 1}.</span>}
                  {typeof action.action === "string" ? humanizeToolField(action.action) : <FrozenValue value={action.action} />}
               </span>
               {Object.hasOwn(action, "delay") && <span className="text-600">{t("toolApproval.redial.delay")}: <FrozenValue value={action.delay} /></span>}
               {Object.hasOwn(action, "maxAttempts") && <span className="text-600">{t("toolApproval.redial.maxAttempts")}: <FrozenValue value={action.maxAttempts} /></span>}
            </div>
            {!!Object.keys(extras).length && <div className="mt-2"><FrozenValue value={extras} humanizeLabels scriptNames={names} /></div>}
         </li>;
      })}
   </ol>;
}

/** Pair outcome definitions with their actions, preserving unmatched actions and unknown fields. */
export default function RedialingRuleToolDetails(props: ToolDetailsProps) {
   const {t} = useTranslation("ask");
   const {parameters, presentation} = props;
   const body = toolRequestBody(parameters);
   const outcomes = body.outcomes;
   const systemActions = body.systemActions;
   if (!Array.isArray(outcomes) || !outcomes.every(outcome => isPresentationRecord(outcome) && typeof outcome.name === "string") ||
      !isPresentationRecord(systemActions) || !Object.values(systemActions).every(actions => Array.isArray(actions) && actions.every(isPresentationRecord)))
      return <ToolPresentationDetails {...props} />;
   const actionMap = systemActions as Record<string, RecordValue[]>;
   const property = (field: PresentedField) => field.path[0] === "body" && body !== parameters ? field.path[1] : field.path[0];
   const fields = presentedFields(parameters, presentation.fields);
   const primary = fields.filter(field => ["name", "outreachMaxAttempts", "outreachMaxAttemptsForNumber"].includes(property(field))).map(field => {
      const name = property(field);
      if (name === "name") return {...field, emphasis: "primary" as const};
      return {...field, label: {text: name === "outreachMaxAttempts"
         ? t("toolApproval.redial.perContact")
         : t("toolApproval.redial.perNumber")}};
   });
   const remaining = fields.filter(field => !["name", "outreachMaxAttempts", "outreachMaxAttemptsForNumber", "outcomes", "systemActions"].includes(property(field)));
   const unmatched = Object.keys(actionMap).filter(name => !outcomes.some(outcome => outcome.name === name));
   const entries = [...outcomes, ...unmatched.map(name => ({name}))];
   return <div className="space-y-5 px-5 pb-5 pt-4">
      <ReviewFields fields={primary.filter(field => property(field) === "name")} />
      <dl className="grid grid-cols-2 gap-3">
         {primary.filter(field => property(field) !== "name").map(field => <div key={JSON.stringify(field.path)} className="rounded-lg bg-100 px-3 py-2.5">
            <dt className="text-xs leading-relaxed text-500">{field.label.text}</dt>
            <dd className="mt-1 text-base font-semibold text-800"><FrozenValue value={field.value} /></dd>
         </div>)}
      </dl>
      <section aria-label={t("toolApproval.redial.outcomes")} className="space-y-3">
         <h3 className="text-xs font-medium text-500">{t("toolApproval.redial.outcomes")}</h3>
         {occurrenceKeys<RecordValue>(entries, valueKey).map(({item: outcome, key}) => {
            const name = outcome.name as string;
            const details = Object.fromEntries(Object.entries(outcome).filter(([key]) => !["name", "definition"].includes(key)));
            return <section key={key} className="space-y-3 rounded-lg border border-neutral p-3">
               <div>
                  <h4 className="text-sm font-semibold text-800 [overflow-wrap:anywhere]" title={name}>{formatOutcomeKeyLabel(name)}</h4>
                  {Object.hasOwn(outcome, "definition") && <p className="mt-1 text-sm leading-relaxed text-600"><FrozenValue value={outcome.definition} humanizeLabels /></p>}
               </div>
               {!!Object.keys(details).length && <FrozenValue value={details} humanizeLabels />}
               {Object.hasOwn(actionMap, name) && (actionMap[name].length
                  ? <RuleActions actions={actionMap[name]} /> : <FrozenValue value={actionMap[name]} />)}
            </section>;
         })}
         {!entries.length && <FrozenValue value={entries} />}
      </section>
      {!!remaining.length && <ReviewFields fields={remaining} />}
   </div>;
}
